import { campaignMetrics, ChartDataResponse, MetricInfo, MetricLabels } from "@api/common";
import { AdUnitFormat, AppState, currentReportCampaign } from "@store";
import * as d3 from "d3";
import { FetchingState } from "rd-redux-http";
import * as React from "react";
import { findDOMNode } from "react-dom";
import { connect } from "react-redux";
import { AdFormatOverviewDetailsReport } from "./AdFormatOverviewDetailsReport";
import "./AdFormatOverviewReport.css";
import { LegendItem } from "./LegendItem";
import { ProportionalProgressChart, ProportionalProgressChartData } from "./ProgressChart";

interface AdFormatOverviewReportComponentProps {
    metrics: MetricInfo[];
    adFormats: AdUnitFormat[];
    data: ChartDataResponse;

    children?: (metric: MetricInfo, dimension: AdUnitFormat, position: number) => React.ReactChild | React.ReactChild[];
}

interface AdFormatOverviewReportState {
    detailsDataIndex: number;
    barCenter: number;
    selectedMetric: MetricInfo;
}

class AdFormatOverviewReportComponent extends React.Component<
    AdFormatOverviewReportComponentProps,
    AdFormatOverviewReportState
> {
    constructor(props: AdFormatOverviewReportComponentProps, context?: any) {
        super(props, context);

        this.state = {
            detailsDataIndex: 0,
            barCenter: -1,
            selectedMetric: props.metrics.find(m => m.isDefault) || props.metrics[0]
        };
    }

    render() {
        const { metrics, adFormats, data, children } = this.props;
        const { selectedMetric, detailsDataIndex } = this.state;

        const percent = calculateAdFormatPercent(adFormats, selectedMetric, data);
        const colorScale = d3
            .scaleLinear()
            .domain([adFormats.length, 0])
            .range([0.1, 1]);

        const adFormatColor = (format: AdUnitFormat): string => {
            const color = d3.color(selectedMetric.color /*"#1a8bb3"*/);
            const index = adFormats.indexOf(format);
            const opacity = colorScale(index);
            color.opacity = opacity;

            return color.toString();
        };

        const progressChartData = adFormats.map<ProportionalProgressChartData>(f => ({
            color: adFormatColor(f),
            value: percent[f.size],
            data: f
        }));

        return (
            <div className="AdFormatOverviewReport">
                <div className="AdFormatOverviewReport__header">
                    <div className="legend">
                        {metrics.filter(m => m.showInLegend).map(metric => (
                            <LegendItem
                                color={metric.color}
                                checked={metric.metric === selectedMetric.metric}
                                onChange={() => this.setState({ selectedMetric: metric })}
                                key={metric.metric}
                            >
                                {MetricLabels.metricSwitcher(metric)}
                            </LegendItem>
                        ))}
                    </div>
                </div>
                <div className="AdFormatOverviewReport__content">
                    <div className="AdFormatOverviewReportLegend AdFormatOverviewReport__legend">
                        {adFormats.map(format => (
                            <div
                                className="AdFormatOverviewReportLegendItem AdFormatOverviewReportLegend__item"
                                key={format.size}
                            >
                                <span
                                    className="AdFormatOverviewReportLegendItem__color"
                                    style={{ backgroundColor: adFormatColor(format) }}
                                />
                                {
                                    <span className="AdFormatOverviewReportLegendItem__value">
                                        {`${(percent[format.size] * 100).toFixed(2)}%`}
                                    </span>
                                }
                                <span className="AdFormatOverviewReportLegendItem__title">{format.name}</span>
                            </div>
                        ))}
                    </div>
                    <div className="AdFormatOverviewReport__chart AdFormatOverviewReportChart">
                        <ProportionalProgressChart
                            className="AdFormatOverviewReportChart__chart"
                            data={progressChartData}
                            onHoverDataItem={(item: ProportionalProgressChartData, barCenter: number) => {
                                this.setState({
                                    detailsDataIndex: Math.max(0, adFormats.indexOf(item.data)),
                                    barCenter
                                });
                            }}
                            ref={instance => {
                                const div = findDOMNode(instance) as HTMLDivElement;

                                if (div && adFormats && adFormats.length && this.state.barCenter < 0) {
                                    const val = percent[Object.keys(percent)[0]];

                                    const rect = div.getBoundingClientRect();

                                    if (rect) {
                                        const firstBarCenter = (rect.width * val) / 2;

                                        if (firstBarCenter !== this.state.barCenter) {
                                            this.setState({ barCenter: firstBarCenter });
                                        }
                                    }
                                }
                            }}
                        />

                        <div className="AdFormatOverviewReportChart__details">
                            {children && children(selectedMetric, adFormats[detailsDataIndex], this.state.barCenter)}
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export const AdFormatOverviewReport = connect(
    (appState: AppState): AdFormatOverviewReportComponentProps => {
        const campaign = currentReportCampaign(appState);
        const metrics = campaignMetrics(campaign ? campaign.unit : "CPM");

        const adUnitsData: ChartDataResponse = FetchingState.getDataOrDefault(appState.reports.chartData.adFormatData, {
            metrics: [],
            dimensions: []
        });

        const adUnitsAdTemplatesData = FetchingState.getDataOrDefault(
            appState.reports.chartData.adFormatAdTemplateData,
            {
                metrics: [],
                dimensions: []
            }
        );

        const adFormats = FetchingState.getDataOrDefault(appState.mappings.adunits, []).filter(adu =>
            adUnitsData.metrics.some(m => m.data.some(d => d["dimension-values"].indexOf(adu.size) !== -1))
        );

        return {
            children: (_: MetricInfo, dimension: AdUnitFormat, barPosition: number) => {
                return (
                    <AdFormatOverviewDetailsReport
                        adUnit={dimension}
                        metrics={metrics}
                        data={adUnitsAdTemplatesData}
                        pointerPosition={barPosition}
                    />
                );
            },
            data: adUnitsData,
            metrics,
            adFormats
        };
    }
)(AdFormatOverviewReportComponent);

function calculateAdFormatPercent(
    adFormats: AdUnitFormat[],
    metric: MetricInfo,
    data: ChartDataResponse
): { [size: string]: number } {
    const metricsData = data.metrics.find(m => m.metric === metric.metric);
    if (!metricsData) {
        return {};
    }

    const values = adFormats.reduce((hash, format) => {
        const val = metricsData.data.find(d => d["dimension-values"].indexOf(format.size) !== -1);

        hash[format.size] = val ? val["metric-value"] : 0;

        return hash;
    }, {});

    const total = Object.keys(values)
        .map(p => values[p])
        .reduce((sum, val) => sum + val, 0);

    return Object.keys(values).reduce((hash, size) => {
        hash[size] = values[size] / Math.max(1, total);
        return hash;
    }, {});
}
